"use client"

import type { ChangeEvent, ComponentPropsWithoutRef, ComponentPropsWithRef, ReactElement, Ref } from "react"
import type { FieldErrors, FieldPath, FieldValues, RegisterOptions, UseFormRegister, UseFormRegisterReturn, UseFormSetValue } from "react-hook-form"

import React, { cloneElement, forwardRef } from "react"
import get from "lodash/get"
import { useFormContext } from "react-hook-form"
import { twJoin } from "tailwind-merge"

import { CollapsableInfo } from "#components/form/collapsableInfo"
import { twMerge } from "#lib/twMergeConfig"
import SvgCheckBold from "#svgs/semantic icons/CheckBold"

export type BaseInputProps<T extends FieldValues, TFieldName extends FieldPath<T>> = {
  placeholder: string | ReactElement
  name: TFieldName
  checkMark?: boolean
  Icon?: React.FC<ComponentPropsWithoutRef<"svg">>
  IconElement?: ReactElement
  hideIcon?: boolean
  collapseInfoInitial?: boolean
  collapsedInfoText?: string
  Info?: ReactElement
  errors?: FieldErrors
  containerStyles?: string
  inputStyles?: string
}

export type ControlledInputProps<T extends FieldValues> = BaseInputProps<T, FieldPath<T>> & {
  setValue?: UseFormSetValue<T>
}

export type UNControlledInputProps<T extends FieldValues, TFieldName extends FieldPath<T> = FieldPath<T>> = BaseInputProps<T, TFieldName> & {
  register?: UseFormRegister<T>
  registerProps?: Omit<RegisterOptions<T, TFieldName>, "onBlur" | "onChange">
}

type InputFieldProps<T extends FieldValues> = UNControlledInputProps<T> &
  Omit<ComponentPropsWithRef<"input">, "placeholder"> & {
    multiline?: boolean
    ignoreRegister?: boolean
  } & Omit<ComponentPropsWithRef<"textarea">, "placeholder">

// TODO remove

export const InputField = forwardRef(function InputField<T extends FieldValues>(
  {
    type = "text",
    placeholder,
    checkMark = true,
    collapseInfoInitial = false,
    collapsedInfoText,
    Info,
    name,
    multiline,
    Icon,
    IconElement,
    hideIcon,
    register,
    className,
    pattern,
    disabled,
    autoCorrect = "off",
    spellCheck = false,
    errors,
    onChange,
    onBlur,
    containerStyles,
    inputStyles,
    registerProps,
    ignoreRegister,
    ...restProps
  }: InputFieldProps<T>,
  ref: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>,
) {
  // TODO register per props rausnehmen und nur noch context? - vlt noch besser RSC unterstützen
  // vlt dafür sorgen das nur entweder der code für textarea oder input mit zum client kommt? - multiline wird sich wahrscheinlich nie aufm client ändern
  const contextState = useFormContext<T>()

  if (contextState) {
    if (!ignoreRegister) register = register ?? contextState.register
    errors = errors ?? contextState.formState.errors
  }

  function onBlurHandler(e: any) {
    e.target.setAttribute("data-touched", "true")
    if (onBlur) onBlur(e)
  }

  let onChangeHandler = onChange

  if (multiline) {
    onChangeHandler = e => {
      calcTextAreaHeight(e as ChangeEvent<HTMLTextAreaElement>)
      onChange?.(e as any)
    }
  }

  let registerObject: UseFormRegisterReturn<typeof name> | undefined

  // TODO write base class for input field without any styling etc - just convenience wrapper for RHF to avoid code duplication
  function preprocessValue(value: string | null) {
    if (value === null || value.length === 0) return undefined

    const valueAsNumber = Number(value)
    if (!Number.isNaN(valueAsNumber)) return valueAsNumber

    return value
  }

  if (register) {
    registerObject = register(name, {
      setValueAs: preprocessValue,
      // ...registerProps,
      onChange: onChangeHandler,
      onBlur: onBlurHandler,
    } as RegisterOptions<T, typeof name>)
  }

  const error = errors ? get(errors, name) : undefined
  const registerSpread = registerObject ?? { onChange: onChangeHandler, onBlur: onBlurHandler }

  const inputProps = {
    ...restProps,
    placeholder: " ",
    "data-touched": false,
    "data-error": !!error,
    disabled: disabled,
    autoCorrect: autoCorrect,
    spellCheck: spellCheck,
    ref: ref as any, // In case user specifies own ref object
    ...registerSpread, // Contains own ref object - override user ref object, register & own ref not possible at the same time
    className:
      "mr-4 peer relative z-10 w-full bg-transparent text-black focus:translate-y-1.5 focus-visible:outline-none disabled:text-gray-70 2xl:focus:translate-y-2.5 [&:not(:placeholder-shown)]:translate-y-1.5 2xl:[&:not(:placeholder-shown)]:translate-y-2.5 autofill:!shadow-[0_0_0px_1000px_#EEF2F7_inset]",
  }

  return (
    <div className={twMerge("grid h-fit", containerStyles)}>
      <div
        data-disabled={disabled}
        className={twMerge(
          "relative z-10 flex min-h-[49px] w-full items-start rounded-[14px] bg-bgray-40 pb-3 pl-6 pr-5 pt-[13px] text-[13px] font-semibold transition-all data-[disabled=true]:bg-gray-10 sm:min-h-[56px] sm:py-4 2xl:min-h-[68px] 2xl:rounded-2xl 2xl:py-[18px] 2xl:pl-8 2xl:pr-6 2xl:text-bg",
          multiline && "pb-6 pt-5 sm:py-6 2xl:py-6",
          className,
        )}>
        {!multiline ? (
          <input {...inputProps} type={type} pattern={pattern} className={twJoin(inputProps.className, "h-6 2xl:h-8", inputStyles)} />
        ) : (
          // TODO wenn textarea mit einem eingabewert initialisiert wird und dieser länger als die höhe ist, wird die höhe erst beim anklicken/tippen aktualisiert - ist das wirklich ein bug oder ein nices feature? - vlt per prop verhalten anpassen
          // @ts-ignore
          <textarea
            {...inputProps}
            className={twMerge(
              inputProps.className,
              "hide-scrollbar mt-1 h-full resize-none focus:translate-y-2.5 2xl:focus:translate-y-3.5 [&:not(:placeholder-shown)]:translate-y-2.5 2xl:[&:not(:placeholder-shown)]:translate-y-3.5",
              inputStyles,
            )}
            onFocus={calcTextAreaHeight}
          />
        )}
        <label className="absolute flex h-6 items-center text-gray-40 transition-all peer-focus:-translate-y-2.5 peer-focus:text-[9px] peer-[&:not(:placeholder-shown)]:-translate-y-2.5 peer-[&:not(:placeholder-shown)]:text-[9px] sm:peer-focus:text-[10px] sm:peer-[&:not(:placeholder-shown)]:text-[10px] 2xl:h-8 2xl:peer-focus:-translate-y-3 2xl:peer-focus:text-[11px] 2xl:peer-[&:not(:placeholder-shown)]:-translate-y-3 2xl:peer-[&:not(:placeholder-shown)]:text-[11px]">
          {placeholder}
        </label>
        {!hideIcon && (
          <>
            {IconElement &&
              cloneElement(IconElement, {
                className: twMerge(
                  "hidden h-6 w-6 min-w-[24px] text-gray-40 peer-disabled:block peer-[&:placeholder-shown]:block peer-[&[data-error=true]]:block peer-[&[data-touched=false]:not(:autofill:-webkit-autofill)]:block 2xl:mt-0.5 2xl:h-7 2xl:w-7",
                  IconElement.props.className,
                ),
              })}
            {Icon && (
              <Icon
                className={
                  "hidden h-6 w-6 min-w-[24px] text-gray-40 peer-disabled:block peer-[&:placeholder-shown]:block peer-[&[data-error=true]]:block peer-[&[data-touched=false]:not(:autofill:-webkit-autofill)]:block 2xl:mt-0.5 2xl:h-7 2xl:w-7"
                }
              />
            )}
            {/* TODO display checkmark only if this field is contained in the dirtyFields object from formState of useFormContext*/}
            {checkMark && (
              <SvgCheckBold
                className={
                  "checkmark mt-px hidden h-5 w-5 min-w-[20px] text-green-50 peer-[&[data-error=false]:not(:placeholder-shown):not(:disabled):-webkit-autofill]:block peer-[&[data-error=false]:not(:placeholder-shown):not(:disabled):autofill]:block peer-[&[data-error=false]:not(:placeholder-shown):not(:disabled):not([data-touched=false])]:block 2xl:h-6 2xl:w-6"
                }
              />
            )}
          </>
        )}
      </div>
      <p
        role={"alert"}
        className={twMerge("ml-6 -translate-y-3.5 text-[11px] font-normal text-red-700 opacity-0 transition-all duration-300", error && "mt-1 translate-y-0 opacity-100")}>
        {error?.message as string}
      </p>
      {!error && Info && (
        <CollapsableInfo collapseInfoInitial={collapseInfoInitial} collapsedInfoText={collapsedInfoText}>
          {Info}
        </CollapsableInfo>
      )}
    </div>
  )
}) as <T extends FieldValues>(p: InputFieldProps<T> & { ref?: Ref<HTMLDivElement> | Ref<HTMLTextAreaElement> }) => ReactElement

function calcTextAreaHeight(e: ChangeEvent<HTMLTextAreaElement>) {
  const textarea = e.target
  textarea.style.height = `${textarea.scrollHeight}px`
}
